import { ref, computed, toRef, reactive, watch } from 'vue';
import { exercisesFacets, resultType } from '@/utils/algolia';
import { searchClient } from '@algolia/client-search';
import { Bus } from '@/utils/bus';

let sharedInstance = null;

export async function useAlgoliaSearch(options) {
  // just return the shared instance if it already exists
  if (!options) {
    // If there's an ongoing fetch, wait for it
    if (sharedInstance?.initialFetchPromise) {
      await sharedInstance.initialFetchPromise;
    }

    return sharedInstance;
  }

  const route = useRoute();
  const { locale } = useI18n();
  const { $config } = useNuxtApp();

  // Extract options with reactive references
  const pageProfile = toRef(options, 'pageProfile');
  const filterPresets = toRef(options, 'filterPresets');

  const state = reactive({
    results: [],
    facetsData: {},
    refinements: {},
    page: 0,
    totalHits: 0,
    totalPages: 0,
    isInitialFetchComplete: false,
  });

  const resultTypeValue = computed(() => resultType(pageProfile.value));
  const searchFacets = computed(() => exercisesFacets(pageProfile.value));
  const hitsPerPage = computed(() => (resultTypeValue.value === 'exercise' ? 6 : 12));

  // Check if filter should be active based on URL
  const isFilterActive = computed(() => {
    const hasFilterParam = route.query.filter === 'true';
    const hasRefinements = Object.keys(route.query).some((key) => key === 'refinements');

    return hasFilterParam || hasRefinements;
  });

  // Computed index name based on result type and locale
  const indexName = computed(() => {
    if (!resultTypeValue.value) return '';
    const prefix = resultTypeValue.value === 'routine' ? 'routine' : 'feg_exercise';
    const localeMap = { 'ch-fr': 'chFr', 'ch-de': 'ch' };
    return prefix + '_' + (localeMap[locale.value] || locale.value);
  });

  // Initialize Algolia client
  const algoliaClient = ref(null);
  if ($config.public) {
    try {
      algoliaClient.value = searchClient($config.public.algoliaApplicationId, $config.public.algoliaSearchApiKey);
    } catch (error) {
      console.error('Error initializing Algolia client:', error);
    }
  }

  // Parse URL parameters to get refinements and page
  function parseUrlParams() {
    const refinements = {};
    let page = 0;

    Object.entries(route.query).forEach(([key, value]) => {
      if (key === 'page') {
        const pageNum = parseInt(value, 10);
        if (!isNaN(pageNum) && pageNum >= 0) {
          page = pageNum;
        }
      } else if (key === 'refinements') {
        value.split(',').forEach((pair) => {
          const [facet, val] = pair.split(':');
          if (facet && val) {
            if (!refinements[facet]) refinements[facet] = [];
            refinements[facet].push(val);
          }
        });
      }
    });

    state.refinements = refinements;
    state.page = page;
  }

  // Update URL with current refinements and page
  function updateUrl(keepPage = false, forceFilterOpen = false) {
    const query = { ...route.query };

    // Clear existing refinements from URL
    Object.keys(query).forEach((key) => {
      if (key === 'refinements' || (!keepPage && key === 'page')) {
        delete query[key];
      }
    });

    // Add current refinements to URL
    const refinementEntries = Object.entries(state.refinements);

    if (refinementEntries.length > 0) {
      // Format as refinements=facet1:value1,facet1:value2,facet2:value3
      const formattedRefinements = [];
      refinementEntries.forEach(([facet, values]) => {
        if (Array.isArray(values)) {
          values.forEach((value) => formattedRefinements.push(`${facet}:${value}`));
        } else {
          formattedRefinements.push(`${facet}:${values}`);
        }
      });

      query.refinements = formattedRefinements.join(',');
    } else if (forceFilterOpen) {
      query.filter = 'true';
    }

    // Add page parameter if not on first page
    if (state.page > 0) {
      query.page = state.page.toString();
    }

    // Update URL without reloading page
    navigateTo({ path: route.path, query }, { replace: true });
  }

  // Toggle a filter value
  function toggleRefinement(facet, value) {
    const newRefinements = { ...state.refinements };

    // Initialize array if needed
    if (!Array.isArray(newRefinements[facet])) {
      newRefinements[facet] = [];
    }

    const valueIndex = newRefinements[facet].indexOf(value);
    if (valueIndex !== -1) {
      // Remove the value if already selected
      newRefinements[facet].splice(valueIndex, 1);

      // delete the facet if no values left
      if (newRefinements[facet].length === 0) {
        delete newRefinements[facet];
      }
    } else {
      // Add the value
      newRefinements[facet].push(value);
    }

    // Update state
    state.refinements = newRefinements;
    state.page = 0;

    // Update URL and fetch data
    updateUrl();
    fetchData();
  }

  // Clear all refinements
  function clearFilters() {
    state.refinements = {};
    state.page = 0;
    updateUrl();
    fetchData();
  }

  // Change the current page
  function changePage(pageNum) {
    if (pageNum >= 0 && pageNum !== state.page) {
      state.page = pageNum;
      updateUrl(true);
      fetchData();
    }
  }

  // Apply preset filters from page configuration
  function applyFilterPresets(forceFilterOpen = false) {
    if (!filterPresets.value) return;

    const newRefinements = { ...state.refinements };

    Object.entries(filterPresets.value).forEach(([facet, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        if (!newRefinements[facet]) newRefinements[facet] = [];

        values.forEach((item) => {
          if (item && item.title) {
            const value = item.title;
            if (!newRefinements[facet].includes(value)) {
              newRefinements[facet].push(value);
            }
          }
        });

        if (newRefinements[facet].length === 0) {
          delete newRefinements[facet];
        }
      }
    });

    state.refinements = newRefinements;
    updateUrl(false, forceFilterOpen);
    fetchData();

    return true;
  }

  // Toggle filter visibility in UI
  function toggleFilter() {
    if (!isFilterActive.value) {
      // If filter not active, activate it
      if (filterPresets.value && Object.keys(filterPresets.value).length > 0) {
        applyFilterPresets(true);
      } else {
        navigateTo({
          path: route.path,
          query: { ...route.query, filter: 'true' },
        });
      }
    } else {
      // If filter is active, just toggle visibility via event
      Bus.$emit('filterVisibilityToggle');
    }
  }

  // Check if a facet value is selected
  function isRefined(facet, value) {
    if (!state.refinements[facet]) return false;

    if (Array.isArray(state.refinements[facet])) {
      return state.refinements[facet].includes(value);
    }

    return state.refinements[facet] === value;
  }

  async function fetchFacetData() {
    if (!algoliaClient.value || !indexName.value) {
      console.error('Cannot fetch facet data: Algolia client not initialized');
      return;
    }

    const searchParams = {
      hitsPerPage: 1,
      facets: searchFacets.value,
      maxValuesPerFacet: 100,
    };

    // get all data to fetch facets
    const response = await algoliaClient.value.search([
      {
        indexName: indexName.value,
        params: searchParams,
      },
    ]);

    if (response?.results?.[0]) {
      const result = response.results[0];
      if (result.facets) {
        Object.entries(result.facets).forEach(([facet, values]) => {
          state.facetsData[facet] = Object.entries(values).map(([label, count]) => ({
            label,
            value: label,
            count,
            isRefined: isRefined(facet, label),
          }));
        });
      }
    }
  }

  // Main data fetching function - handles both facets and results
  async function fetchData() {
    if (!algoliaClient.value || !indexName.value) {
      console.error('Cannot fetch data: Algolia client not initialized');
      return;
    }

    try {
      // Build facet filters
      const facetGroups = {};
      Object.entries(state.refinements).forEach(([facet, values]) => {
        if (!facetGroups[facet]) facetGroups[facet] = [];

        if (Array.isArray(values)) {
          values.forEach((value) => {
            facetGroups[facet].push(`${facet}:${value}`);
          });
        } else {
          facetGroups[facet].push(`${facet}:${values}`);
        }
      });

      // Convert to Algolia filter format (arrays = OR, multiple arrays = AND)
      const facetFilters = Object.values(facetGroups).map((group) => {
        return group.length === 1 ? group[0] : group;
      });

      // Search parameters
      const searchParams = {
        hitsPerPage: hitsPerPage.value,
        page: state.page,
        facets: searchFacets.value,
        maxValuesPerFacet: 100,
      };

      if (facetFilters.length > 0) {
        searchParams.facetFilters = facetFilters;
      }

      // Execute search
      const response = await algoliaClient.value.search([
        {
          indexName: indexName.value,
          params: searchParams,
        },
      ]);

      // Process results
      if (response?.results?.[0]) {
        const result = response.results[0];

        // Update results state
        state.results = result.hits || [];
        state.totalHits = result.nbHits || 0;
        state.totalPages = result.nbPages || 0;
      }
    } catch (error) {
      console.error('Algolia search error:', error);
    } finally {
      state.isInitialFetchComplete = true;
    }
  }

  // Helper to get and sort facet values
  function getFacetValues(facet) {
    const values = state.facetsData[facet] || [];

    return [...values].sort((a, b) => {
      // Sort refined first, then by count
      if (a.isRefined !== b.isRefined) {
        return a.isRefined ? -1 : 1;
      }
      return b.count - a.count;
    });
  }

  let initialFetchPromise = Promise.resolve();

  // Initial data load
  if (algoliaClient.value) {
    const facetPromise = fetchFacetData();
    const dataPromise = fetchData();

    initialFetchPromise = Promise.all([facetPromise, dataPromise]).catch((error) => {
      console.error('Error during initial data fetch:', error);
      // Always resolve to prevent hanging
      return null;
    });
    parseUrlParams();
  }

  sharedInstance = {
    results: computed(() => state.results),
    state,
    isInitialFetchComplete: computed(() => state.isInitialFetchComplete),
    initialFetchPromise,

    // Filter properties
    indexName,
    isFilterActive,
    searchFacets,
    resultType: resultTypeValue,
    hitsPerPage,

    // Filter operations
    toggleFilter,
    clearFilters,
    toggleRefinement,
    applyFilterPresets,

    // Result access
    getFacetValues,
    isRefined,
    hasRefinements: computed(() => Object.keys(state.refinements).length > 0),
    refinements: computed(() => state.refinements),

    // Pagination
    changePage,
    currentPage: computed(() => state.page),
    totalPages: computed(() => state.totalPages),
    totalHits: computed(() => state.totalHits),

    fetchData,
  };

  // Wait for initial fetch to complete before returning
  await initialFetchPromise;

  return sharedInstance;
}
